/**
 * Centralized color definitions for the application
 * Use these constants instead of hardcoded hex values
 * Source of truth: designTokens.json (hotelspot DS, https://hotelspot-tokens.web.app/),
 * shared with tailwind.config.js
 */

import designTokens from './designTokens.json';

// Design system tokens (names as in hotelspot-audit: bg-surface, text-secondary, accent-ink...)
export const ds = designTokens.color;
export const dsRadius = designTokens.radius;
export const dsShadow = designTokens.shadow;

export const colors = {
  // Primary (accent-ink) - main buttons, active tabs, step numbers
  primary: {
    50: ds.bgMuted,
    100: ds.borderDefault,
    200: ds.borderStrong,
    300: ds.textMuted,
    400: ds.textSecondary,
    500: ds.accentInk, // Main primary color
    600: ds.accentInkHover, // Hover (as hover:opacity-90 in audit)
    700: ds.textPrimary,
    800: ds.textPrimary,
    900: ds.textPrimary,
  },

  // Success (Green)
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },

  // Danger (Red)
  danger: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },

  // Warning (Amber)
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },

  // Neutral (Gray)
  neutral: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },

  // Info (Blue) - for informational elements
  info: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },

  // Purple - for special elements
  purple: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',
    600: '#9333ea',
    700: '#7e22ce',
    800: '#6b21a8',
    900: '#581c87',
  },

  // Violet (Tailwind violet) - plakietka LOCALHOST w pasku górnym
  violet: {
    50: '#f5f3ff',
    100: '#ede9fe',
    200: '#ddd6fe',
    300: '#c4b5fd',
    400: '#a78bfa',
    500: '#8b5cf6',
    600: '#7c3aed',
    700: '#6d28d9',
    800: '#5b21b6',
    900: '#4c1d95',
  },

  // Pink
  pink: {
    50: '#fdf2f8',
    100: '#fce7f3',
    200: '#fbcfe8',
    300: '#f9a8d4',
    400: '#f472b6',
    500: '#ec4899',
    600: '#db2777',
    700: '#be185d',
    800: '#9d174d',
    900: '#831843',
  },

  // Teal
  teal: {
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#14b8a6',
    600: '#0d9488',
    700: '#0f766e',
    800: '#115e59',
    900: '#134e4a',
  },

  // Common colors
  white: ds.bgSurface,
  black: '#000000',
  transparent: 'transparent',
} as const;

// Chart color palette - ordered for visual distinction
// Base: accent-ink and DS grays
export const chartColors = {
  primary: ds.accentInk,
  primaryDark: ds.textPrimary,
  primaryLight: ds.textSecondary,
  primaryGradientStart: ds.accentInk,
  primaryGradientEnd: ds.textPrimary,

  // Semantic chart colors
  series: [
    ds.accentInk,
    ds.textSecondary,
    ds.textMuted,
    colors.warning[500], // Amber
    colors.danger[500], // Red
    colors.purple[500], // Purple
    colors.pink[500], // Pink
    colors.success[500], // Green
  ],

  // For area/bar gradients (matching sidebar gradient)
  gradient: {
    primary: {
      start: ds.accentInk,
      end: ds.textPrimary,
    },
    success: {
      start: colors.success[500],
      end: colors.success[600],
    },
  },

  // Shadow color for charts
  shadow: {
    primary: ds.accentInk,
    opacity: 0.12,
  },

  // Heat map scale (light to dark, DS grays to accent-ink)
  heatmap: [
    ds.bgMuted,
    ds.borderStrong,
    ds.textSecondary,
    ds.accentInk,
  ],
} as const;

// Event colors for analytics
export const eventColors: Record<string, string> = {
  BUSINESS_CARD_SCREEN_OPENED: colors.primary[500],
  ATTRACTION_SCREEN_OPENED: colors.success[500],
  MAP_SCREEN_OPENED: colors.warning[500],
  HOTEL_EVENTS_SCREEN_OPENED: colors.danger[500],
  SPECIAL_OFFER_SCREEN_OPENED: colors.purple[500],
  WELCOME_SCREEN_START_BUTTON_TAPPED: colors.pink[500],
};

// Type exports
export type Colors = typeof colors;
export type ChartColors = typeof chartColors;
export type ColorShade = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900;
